import { Radar, ShieldOff } from "lucide-react";

import {
  useCandidateRuntimeDetectionQuery,
  type CandidateFinalQuestioning,
  type CandidateRuntimeDetection,
} from "@/api/finalQuestioning";
import { Badge } from "@/ui/Badge";
import {
  refusalClause,
  refusalRecovery,
} from "@/ui/preview/finalQuestioningRefusal";

/**
 * Runtime safety detection, displayed SEPARATELY from the Project's own final
 * safety-questioning choice, as read-only target-derived state (#853).
 *
 * ## WHY IT IS A SEPARATE BLOCK
 *
 * The Lovable reference has one switch covering both, and the two are
 * independent mechanisms with different owners. The Project's final safety
 * questioning is a clinical choice this workspace makes and records
 * (`ui/project/FinalQuestioningControl`). Per-message runtime safety detection
 * is the deployment target's own policy: the Editor does not author it, does
 * not store it and — contract §5 — does not observe it. Rendering them as one
 * control implies this screen can change the second, which is the misreading
 * the ticket exists to correct.
 *
 * ## WHY THE OUTCOME IS READ, NOT COMPUTED
 *
 * §8's admission table is one rule with two consumers, and the Editor Service
 * owns it. This component renders `decision` from
 * `GET …/preview-candidates/{id}/runtime-detection`; it has no branch that can
 * turn a refusal into an admission, it supplies no observation, and it does not
 * re-derive compatibility from the posture and the observed state it displays.
 * The `notApproval` sentence is the server's own fixed text, rendered beside
 * every outcome so a compatible posture cannot be read as clinical approval or
 * as authorization to deploy.
 *
 * ## WHAT `observed` NOW MEANS
 *
 * The Editor still authors and stores no detector policy. It ASKS the
 * authenticated target — over the same service-auth lane a launch uses, and
 * only for a candidate whose frozen posture requires compatible detection — and
 * the answer is the target's own. `observationSource` is what keeps the three
 * apart, because an `unknown` alone would collapse them: nothing was asked
 * (the candidate demands nothing of the target's detector), the target
 * answered, or the read could not be made. `observation` carries the target's
 * OWN coordinates, which are not the Project's `targetId`/`targetRevision`
 * beside them and are never rendered as though they were.
 *
 * ## THE THREE ANSWERS IT KEEPS APART
 *
 * A frozen candidate, a compatible posture and a deployment approval are
 * different things (consumer contract §4), and so are the two postures on this
 * screen: what the Project's DRAFT currently says, and what this immutable
 * candidate FROZE. `draftEffective` is passed in so the block can state when
 * they differ — a candidate frozen while final questioning was on does not
 * start reporting off because somebody disabled it afterwards, and a clinician
 * about to launch it needs to know which one they are launching.
 *
 * ## WHAT ENFORCES IT
 *
 * Not this screen. liv1.0#1216 owns the real preview admission — the session it
 * refuses to start and what happens on a refusal — and it decides against a
 * FRESH observation of its own at dispatch. So what is rendered here is a
 * PRE-FLIGHT: what a launch would decide right now, with the same evidence the
 * launch would compose. Anything that moves in between is refused there rather
 * than admitted on this answer, and the copy says that rather than implying
 * this screen gates anything.
 */
export function RuntimeSafetyDetection({
  projectId,
  candidateId,
  candidateLabel,
  draftEffective,
  postureRecorded,
}: {
  projectId: string;
  candidateId: string;
  /** "Test version 3", from the listing's own server-issued revision. */
  candidateLabel: string;
  /**
   * What the Project's DRAFT says today, or null when that read has not
   * settled. Never inferred from the candidate: the two are different facts,
   * which is the whole point of showing both.
   */
  draftEffective: "enabled" | "disabled" | null;
  /**
   * Whether the LISTING carried a posture for this candidate — that is, whether
   * it was frozen after the posture began being recorded at all.
   *
   * Asked here rather than discovered by fetching, because the service refuses
   * such a candidate with a 404 by design ("the posture was never recorded" and
   * "final questioning is on" are different facts), and a request whose only
   * possible answer is a refusal is a request not worth making. The absence is
   * still STATED below — never rendered as nothing, which is the reading that
   * would let an unexamined candidate pass as safe.
   */
  postureRecorded: boolean;
}) {
  const query = useCandidateRuntimeDetectionQuery(
    projectId,
    postureRecorded ? candidateId : null,
  );
  const state = query.data;

  if (!postureRecorded) {
    return (
      <section
        aria-label="Runtime safety detection"
        data-slot="runtime-safety-detection"
        className="rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
      >
        {candidateLabel} was frozen before this workspace recorded what a test
        version does about final safety questioning, so its safety posture is
        unknown and no compatibility statement can be made about it. Freeze
        again to get one.
      </section>
    );
  }

  // `decision` is required by the contract, so an answer without one is a
  // malformed body rather than a state — read defensively all the same, since
  // the alternative is a thrown render that takes the whole preview dialog with
  // it. Reported as unread, which is what it is.
  if (state !== undefined && state.decision === undefined) {
    return (
      <section
        aria-label="Runtime safety detection"
        data-slot="runtime-safety-detection"
        className="rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
      >
        The Editor Service did not state how this test version&rsquo;s safety
        posture stands against the preview target. Nothing has been assumed in
        either direction.
      </section>
    );
  }

  if (state === undefined) {
    return (
      <section
        aria-label="Runtime safety detection"
        data-slot="runtime-safety-detection"
        className="rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
      >
        {query.isPending
          ? "Reading how this test version's safety posture stands against the preview target…"
          : "How this test version's safety posture stands against the preview target could not be read. Nothing has been assumed in either direction."}
      </section>
    );
  }

  const refused = state.decision.outcome === "refuse";
  const candidateOff = state.finalQuestioning.effective === "disabled";
  const drifted = draftEffective !== null && draftEffective !== state.finalQuestioning.effective;

  return (
    <section
      aria-label="Runtime safety detection"
      data-slot="runtime-safety-detection"
      className="space-y-2 rounded-md border border-border bg-card px-4 py-3 text-sm"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex items-center gap-2 font-semibold text-foreground">
          <Radar aria-hidden="true" className="h-4 w-4" />
          Runtime safety detection
        </span>
        {/* The badge repeats the state IN WORDS; the glyph beside it is
            decorative and the tone carries nothing on its own.

            THREE LABELS, NOT TWO. "No requirement" was written when an
            `admit` could only mean a candidate that asks nothing of the
            target's detector — which was true while this deployment observed
            nothing and every MOD-equivalent candidate was refused. It is not
            true now: a candidate frozen with final safety questioning OFF is
            admitted when the target's detection satisfies what it REQUIRES,
            and labelling that "No requirement" contradicts both the sentence
            below it and the candidate's own
            `requiresCompatibleRuntimeDetection`. */}
        <Badge tone={refused ? "warning" : "outline"}>
          {refused ? (
            <ShieldOff aria-hidden="true" className="me-1 h-3 w-3" />
          ) : null}
          {refused
            ? "Preview would be refused"
            : state.finalQuestioning.requiresCompatibleRuntimeDetection
              ? "Requirement met"
              : "No requirement"}
        </Badge>
        <span className="text-xs text-muted-foreground">
          Target{" "}
          <code className="rounded bg-secondary px-1 font-mono">
            {state.targetId}
          </code>{" "}
          · revision {state.targetRevision}
        </span>
      </div>

      <p className="text-muted-foreground">{observation(state)}</p>

      {state.observation === undefined || state.observation === null ? null : (
        /* The TARGET's own coordinates, labelled as the target's — never
           merged with the Project's target row above, which counts retargets
           in this workspace and is a different identity.

           A definition list rather than a sentence: these are three long
           opaque values an operator compares against the target's own logs,
           and `break-all` is what keeps them wrapping INSIDE the card instead
           of widening it — the Lovable panel is a fixed-width column. */
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <dt>Target reported</dt>
          <dd className="break-all font-mono">
            {state.observation.targetId ?? "no compiled configuration to name"}
          </dd>
          {state.observation.targetRevision === undefined ||
          state.observation.targetRevision === null ? null : (
            <>
              <dt>Configuration</dt>
              <dd className="break-all font-mono">
                {state.observation.targetRevision}
              </dd>
            </>
          )}
          {state.observation.detectorPolicyRevision === undefined ||
          state.observation.detectorPolicyRevision === null ? null : (
            <>
              <dt>Detection policy</dt>
              <dd className="break-all font-mono">
                {state.observation.detectorPolicyRevision}
              </dd>
            </>
          )}
        </dl>
      )}

      <p className="text-foreground">
        {outcomeSentence(state, candidateLabel)}
      </p>

      {refused && recovery(state) !== null ? (
        <p className="text-muted-foreground">{recovery(state)}</p>
      ) : null}

      <p className="text-muted-foreground">
        {postureSentence(state.finalQuestioning, candidateLabel)}
        {drifted
          ? ` This project's draft now says final safety questioning is ${
              draftEffective === "disabled" ? "off" : "on"
            }, which is not what this test version froze. Freezing again is what carries the current choice.`
          : ""}
      </p>

      {/* The server's fixed sentence, rendered beside every outcome — an
          `admit` most of all. A consumer must not be able to show a compatible
          posture without it. */}
      <p className="text-xs text-muted-foreground">
        {state.decision.notApproval} Real preview admission is enforced by the
        deployment target&rsquo;s own service, not by this screen, and it
        decides again when a preview is launched &mdash; so this describes the
        target as it is now, not a guarantee about the next launch.
      </p>

      {candidateOff ? null : (
        <p className="text-xs text-muted-foreground">
          This test version runs its final safety questioning, so it asks
          nothing of the target&rsquo;s per-message detection either way.
        </p>
      )}
    </section>
  );
}

/**
 * WHERE THE OBSERVATION CAME FROM, in words, because "unknown" alone is
 * ambiguous in exactly the way that matters: it could mean the target answered
 * and said it did not know, or that nobody asked. Those are different facts
 * with different recoveries, and the server distinguishes them so this does
 * not have to guess.
 *
 * An unrecognized source falls through to the last sentence rather than to a
 * reassuring default: a value from a newer service must not be rendered as if
 * this build understood it.
 */
function observation(state: CandidateRuntimeDetection): string {
  switch (state.observationSource) {
    case "not_observed_by_editor":
      return "This test version asks nothing of the target's per-message safety detection, so this workspace did not read it. It is the target's own policy and is neither authored nor changed here.";
    case "observed_from_target":
      return state.observed === "unknown"
        ? "The preview target was asked and could not establish its own per-message safety detection. That is the target's answer, not a guess made here, and it is never read as the detection being on."
        : `The preview target reports its per-message safety detection as ${state.observed}. It is the target's own policy: read here, never authored or changed by this workspace.`;
    case "target_unreadable":
      return "The preview target's per-message safety detection could not be read from this deployment — no credential for the target, the target not serving previews, or an answer this workspace cannot interpret. Nothing has been assumed in either direction.";
    default:
      return "Where this test version's safety posture stands against the preview target could not be established. Nothing has been assumed in either direction.";
  }
}

/**
 * The next act, when there is one for this reader. `null` where there is not:
 * the target's runtime safety detection is the deployment target's own policy,
 * and this workspace cannot change it — an instruction to enable it would be
 * one the clinician cannot carry out.
 */
function recovery(state: CandidateRuntimeDetection): string | null {
  return state.decision.outcome === "refuse"
    ? refusalRecovery(state.decision.reason ?? undefined)
    : null;
}

/**
 * The DECISION, in a sentence, from the server's own outcome and reason. Every
 * branch below is a reason the service actually returned; there is no default
 * that admits.
 */
function outcomeSentence(
  state: CandidateRuntimeDetection,
  candidateLabel: string,
): string {
  if (state.decision.outcome === "admit") {
    return state.finalQuestioning.requiresCompatibleRuntimeDetection
      ? `${candidateLabel} was frozen with final safety questioning off, and the target's per-message detection currently satisfies what it requires — so launching it now would not be refused on safety grounds.`
      : `${candidateLabel} asks nothing of the target's per-message detection, so a real preview of it is not refused on safety grounds.`;
  }
  // The clause is the ONE transcription of the server's refusal vocabulary,
  // shared with the launch failure so the two surfaces cannot describe the
  // same refusal differently — see `finalQuestioningRefusal`.
  return `${candidateLabel} was frozen with final safety questioning off, so a real preview of it requires the target's per-message detection to be enabled and compatible. Launching it now would be refused before a session starts: ${refusalClause(state.decision.reason ?? undefined)}.`;
}

function postureSentence(
  posture: CandidateFinalQuestioning,
  candidateLabel: string,
): string {
  if (posture.effective === "disabled") {
    return `${candidateLabel} froze with final safety questioning off — the pack it carries drops the final safety track. That posture is immutable: it is what this test version does, whatever the project decides afterwards.`;
  }
  return posture.state === "enabled"
    ? `${candidateLabel} froze with final safety questioning on, explicitly chosen for this project.`
    : `${candidateLabel} froze with final safety questioning on, inherited from the base pack.`;
}
